import type { ReactNode } from "react";
import { ArrowLeft, ChevronRight, Save, CheckCircle2, Info } from "lucide-react";
import { cn } from "../../lib/utils";
import { 
  HorizontalStack, 
  VerticalStack, 
} from "../blocks";

export interface WizardTab {
  id: string;
  label: string;
  icon: ReactNode;
  count?: number;
  disabled?: boolean;
}

export interface WizardWorkspaceProps {
  /** Visibility & State */
  isOpen: boolean;
  isLoading?: boolean;
  isSaving?: boolean;

  /** Header Identity */
  title: string;
  subtitle?: string | null;

  /** Navigation (Tabs) */
  tabs: WizardTab[];
  activeTabId: string;
  onTabChange: (id: string) => void;

  /** Actions */
  saveLabel?: string;
  onSave: () => void;
  onClose: () => void;
  discardLabel?: string;

  /** Health / Summary Panel (Optional) */
  summaryEyebrow?: string;
  summaryItems?: Array<{
    label: string;
    value: string;
    complete?: boolean;
  }>;
  summaryCallout?: string | null;

  /** Content */
  children: ReactNode;

  /** Styling */
  className?: string;
  maxContentWidth?: string;

  /** When true the content area has no padding and does not scroll — the children own the layout */
  fillContent?: boolean;
}

export interface WizardWorkspaceContentProps {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  maxContentWidth?: string;
}

export function WizardWorkspaceContent({
  children,
  className,
  contentClassName,
  maxContentWidth = "max-w-4xl",
}: WizardWorkspaceContentProps) {
  return (
    <div className={cn("flex h-full min-h-0 flex-col", className)}>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div
          className={cn(
            "mx-auto w-full px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6",
            maxContentWidth,
            contentClassName
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * WizardWorkspace Blueprint
 * 
 * CODIFIED BLUEPRINT for multi-step creation/edit flows.
 * Enforces the "Immersive Designer" aesthetic:
 * 1. Workspace-filling layout.
 * 2. Immersive header with prominent Save/Discard actions.
 * 3. Left-hand navigation sidebar with "Design Health" status.
 * 4. Step-by-step footer navigation.
 */
export function WizardWorkspace({
  isOpen,
  isLoading,
  isSaving,
  title,
  subtitle,
  tabs,
  activeTabId,
  onTabChange,
  saveLabel = "Save Changes",
  onSave,
  onClose,
  discardLabel = "Discard Changes",
  summaryEyebrow = "Design Health",
  summaryItems,
  summaryCallout,
  children,
  className,
  maxContentWidth = "max-w-[1600px]",
  fillContent = false,
}: WizardWorkspaceProps) {
  if (!isOpen) {
    return null;
  }

  const activeTabIndex = tabs.findIndex((t) => t.id === activeTabId);
  const isFirstTab = activeTabIndex === 0;
  const isLastTab = activeTabIndex === tabs.length - 1;

  const handleNext = () => {
    if (!isLastTab) {
      onTabChange(tabs[activeTabIndex + 1].id);
    }
  };

  const handlePrev = () => {
    if (!isFirstTab) {
      onTabChange(tabs[activeTabIndex - 1].id);
    }
  };

  return (
    <div
      className={cn(
        "relative z-50 flex h-full max-h-full min-h-0 flex-1 flex-col overflow-hidden bg-background animate-in fade-in duration-300",
        className
      )}
    >
      {/* Immersive Header */}
      <header className="shrink-0 border-b border-border/60 bg-muted/5 px-4 py-2 sm:px-6 sm:py-3">
        <div className="flex min-w-0 items-center justify-between gap-2">
          {/* Left: close + identity */}
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4">
            <button
              onClick={onClose}
              className="group flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border/60 transition-all hover:bg-muted"
            >
              <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            </button>
            <div className="min-w-0">
              <h1 className="truncate text-base font-black tracking-tight sm:text-xl">{title}</h1>
              {subtitle && (
                <p className="hidden truncate text-[10px] font-bold text-muted-foreground/80 sm:block">
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {/* Right: actions */}
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
            <button
              onClick={onClose}
              className="hidden px-3 py-1.5 text-xs font-bold text-muted-foreground transition-colors hover:text-foreground sm:block sm:px-4 sm:py-2"
            >
              {discardLabel}
            </button>
            <button
              onClick={() => onSave()}
              disabled={isSaving || isLoading}
              className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-black text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:opacity-90 disabled:opacity-50 sm:gap-2 sm:px-6 sm:py-2"
            >
              {isSaving ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
              ) : (
                <>
                  <Save className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{saveLabel}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile tab nav — icon-only, visible below lg breakpoint */}
      <nav className="flex shrink-0 border-b border-border/40 bg-muted/5 px-2 py-1 gap-0.5 lg:hidden">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            disabled={tab.disabled}
            title={tab.label}
            className={cn(
              "relative flex h-9 w-9 shrink-0 items-center justify-center rounded-md transition-all disabled:opacity-50",
              activeTabId === tab.id
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            {tab.icon}
            {tab.count !== undefined && tab.count > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary px-0.5 text-[8px] font-black text-primary-foreground ring-1 ring-background">
                {tab.count > 9 ? "9+" : tab.count}
              </span>
            )}
          </button>
        ))}
      </nav>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Navigation Sidebar — desktop only */}
        <aside className="hidden lg:flex min-h-0 w-64 shrink-0 flex-col gap-8 overflow-hidden border-r border-border/40 bg-muted/5 p-4">
          <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain pr-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                disabled={tab.disabled}
                className={cn(
                  "flex items-center justify-between rounded-lg px-4 py-3 text-sm font-bold transition-all",
                  activeTabId === tab.id
                    ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                )}
              >
                <HorizontalStack gap={3} className="items-center">
                  <span
                    className={cn(
                      "transition-colors",
                      activeTabId === tab.id
                        ? "text-primary-foreground"
                        : "text-muted-foreground/60"
                    )}
                  >
                    {tab.icon}
                  </span>
                  {tab.label}
                </HorizontalStack>
                {tab.count !== undefined && (
                  <span
                    className={cn(
                      "rounded-md px-1.5 py-0.5 text-[10px] font-black",
                      activeTabId === tab.id
                        ? "bg-primary-foreground/20 text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    )}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {(summaryItems || summaryCallout) && (
            <VerticalStack gap={4} className="shrink-0 pb-4">
              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 px-2">
                {summaryEyebrow}
              </p>
              {summaryItems && (
                <div className="space-y-3 px-2">
                  {summaryItems.map((item, idx) => (
                    <VerticalStack key={idx} gap={1}>
                      <HorizontalStack className="items-center justify-between">
                        <span className="text-[10px] font-bold text-muted-foreground/80">
                          {item.label}
                        </span>
                        {item.complete !== undefined && (
                          item.complete ? (
                            <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                          ) : (
                            <Info className="h-3 w-3 text-amber-500" />
                          )
                        )}
                      </HorizontalStack>
                      <p className="text-[11px] font-black text-foreground">
                        {item.value}
                      </p>
                    </VerticalStack>
                  ))}
                </div>
              )}
              {summaryCallout && (
                <div className="mx-2 rounded-lg bg-amber-500/10 border border-amber-500/20 p-3">
                  <p className="text-[10px] font-bold text-amber-700 leading-relaxed">
                    {summaryCallout}
                  </p>
                </div>
              )}
            </VerticalStack>
          )}
        </aside>

        {/* Main Content Area */}
        <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
          {fillContent ? (
            <div className="min-h-0 flex-1 overflow-hidden">
              {children}
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6">
              <div className={cn("mx-auto w-full", maxContentWidth)}>
                {children}
              </div>
            </div>
          )}

          {/* Persistent Footer Navigation */}
          <footer className="shrink-0 border-t border-border/40 bg-background px-4 py-2 sm:px-8 sm:py-3">
            <div className={cn("mx-auto flex w-full items-center justify-between", maxContentWidth)}>
              <div>
                {!isFirstTab && (
                  <button
                    onClick={handlePrev}
                    className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-black text-muted-foreground transition-all hover:bg-muted/40 hover:text-foreground sm:gap-2 sm:px-6 sm:py-3 sm:text-sm"
                  >
                    <ArrowLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    <span className="sm:hidden">Back</span>
                    <span className="hidden sm:inline">Back to {tabs[activeTabIndex - 1].label}</span>
                  </button>
                )}
              </div>
              <div>
                {!isLastTab && (
                  <button
                    onClick={handleNext}
                    className="flex items-center gap-1.5 rounded-lg bg-muted px-3 py-2 text-xs font-black text-foreground transition-all hover:bg-muted/80 sm:gap-2 sm:px-6 sm:py-3 sm:text-sm"
                  >
                    <span className="sm:hidden">Next</span>
                    <span className="hidden sm:inline">Next: {tabs[activeTabIndex + 1].label}</span>
                    <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                )}
              </div>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
}
